/**
 * Phone frame vs. wide layout on a desktop browser.
 *
 * On a big screen the app defaults to a centred phone frame — that is the
 * shape the camera, the record dock and the editor were designed for, and it
 * is what you want when you are actually recording. `wide` lets the same app
 * use the whole window instead, which is what you want when you are reading a
 * feed of notes, writing, or working through a project list.
 *
 * The preference is only meaningful above the desktop breakpoint: a phone is
 * already the phone layout, so the toggle is hidden there rather than offering
 * a choice that does nothing.
 *
 * Stored in localStorage and mirrored onto `<html data-layout>`, which is what
 * the CSS keys off. index.html applies the stored value in an inline script
 * before the first paint, so a wide-mode reload never flashes the phone frame.
 */

export type LayoutMode = 'phone' | 'wide'

export const LAYOUT_KEY = 'okbMe.layout'

/** Must match the `min-width` the desktop rules in global.css use. */
export const DESKTOP_MIN_WIDTH = 720

export function isLayoutMode(value: unknown): value is LayoutMode {
  return value === 'phone' || value === 'wide'
}

export function readLayoutMode(): LayoutMode {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY)
    if (isLayoutMode(stored)) return stored
  } catch {
    // Private mode / blocked storage — the default is fine.
  }
  return 'phone'
}

/** True when the window is wide enough for the choice to change anything. */
export function layoutToggleApplies(): boolean {
  return typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH
}

/** Mirror the mode onto <html> so the stylesheets can see it. */
export function applyLayoutMode(mode: LayoutMode): void {
  document.documentElement.dataset.layout = mode
}

export function writeLayoutMode(mode: LayoutMode): LayoutMode {
  try {
    localStorage.setItem(LAYOUT_KEY, mode)
  } catch {
    // Best-effort: the mode still applies for this session.
  }
  applyLayoutMode(mode)
  return mode
}

export function toggleLayoutMode(): LayoutMode {
  return writeLayoutMode(readLayoutMode() === 'wide' ? 'phone' : 'wide')
}
